import { RefObject, useEffect, useState } from 'react';

const videoHasSource = (video: HTMLVideoElement) =>
	Boolean(video.currentSrc) ||
	Boolean(video.getAttribute('src')) ||
	Boolean(video.querySelector('source[src]'));

const isElementInViewport = (el: Element) => {
	const rect = el.getBoundingClientRect();
	return (
		rect.bottom > 0 && rect.top < (window.innerHeight || document.documentElement.clientHeight)
	);
};

export const useLazyVideoSource = (ref: RefObject<Element | null>, rootMargin = '400px') => {
	const [shouldLoad, setShouldLoad] = useState(false);

	useEffect(() => {
		if (shouldLoad) return;

		const el = ref.current;
		if (!el) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setShouldLoad(true);
					observer.disconnect();
				}
			},
			{ rootMargin }
		);

		observer.observe(el);

		return () => observer.disconnect();
	}, [ref, shouldLoad, rootMargin]);

	useEffect(() => {
		if (!shouldLoad) return;

		const root = ref.current;
		if (!root) return;

		const videos = root.querySelectorAll<HTMLVideoElement>('video');

		videos.forEach(video => {
			if (!videoHasSource(video)) return;
			video.load();
		});

		if (!isElementInViewport(root)) return;

		videos.forEach(video => {
			if (!videoHasSource(video)) return;
			video.play().catch(() => {});
		});
	}, [shouldLoad, ref]);

	return shouldLoad;
};
